/**
 * Legacy GlobalNavigation hook for the Invited App Shortcuts GRS recommendations query
 * (productionisation cohort — PUMA-2012).
 *
 * Unlike the GSN surface (PUMA-2011), which reads the query with platform Relay
 * (`useLazyLoadQuery` on the ambient AGG environment inside `GlobalSideNavigationAGGRelayConnection`),
 * the legacy nav is NOT wrapped in that environment and already reads its own
 * `GlobalNavigationComponentQuery` through `@confluence/apollo-relay-interop`. So this hook uses the
 * same interop path:
 *
 *   1. `preloadGlobalNavigationComponent` `queryAgg`s this operation at route resolution (SSR),
 *      seeding the interop (Apollo AGG) cache.
 *   2. This hook reads the SAME operation + variables with
 *      `useQueryInterop({ interopApolloAggClient: true })`, so the SSR-preloaded record hydrates the
 *      client cache and is served without a second network request.
 *
 * The hook is deliberately parent-level (called from `GlobalNavigationComponent`) rather than a
 * self-contained renderer: the parent needs `recommendedApps` to decide BOTH whether to render the
 * decorated invited-app nudge AND whether to hand the Jira shortcut over from the main
 * `GlobalAppShortcuts` section (which strips it when the treatment is active). Keeping the read here
 * means both decisions use the same value.
 *
 * Everything downstream of the read — GRS response translation, the shared cookie cache singleton,
 * the productionisation resolver and exposure firing — reuses the same helpers as the GSN renderer
 * so the two surfaces stay behaviourally identical (they differ only in how the query is read).
 */
import { useEffect, useMemo } from 'react';

import { isAllowedTargetAppForInvitedAppShortcuts } from '@atlassian/cross-join-nudges/common/utils/invited-app-shortcuts-utils';
import {
	useInvitedAppShortcuts,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import type { InvitedAppShortcutAllowedTargetAppKey } from '@atlassian/cross-join-nudges/types';
import { buildInvitedAppShortcutsRecommendationsResult } from '@atlassian/cross-join-nudges/ui/invited-app-shortcuts/invited-app-shortcuts-recommendations-provider';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';

import { getInvitedAppShortcutsCache } from '../PlatformNavigation/AppShortcuts/hooks/useResolveInvitedAppShortcuts.tsx';
import { InvitedAppShortcutsRecommendationsQueryNode } from '../PlatformNavigation/AppShortcuts/InvitedAppShortcutsRecommendations.tsx';
import { buildInvitedAppShortcutsRecommendationsVariables } from '../PlatformNavigation/AppShortcuts/invitedAppShortcutsRecommendationsVariables.ts';
import type {
	InvitedAppShortcutsRecommendationsQuery$data,
	InvitedAppShortcutsRecommendationsQuery$variables,
} from '../PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

const SOURCE_APP = 'confluence' as const;

export type UseLegacyInvitedAppShortcutsRecommendationsArgs = {
	cloudId: string;
	userId: string;
	/**
	 * Whether the user is in the gated productionisation cohort — i.e. BOTH experiment arms (treatment
	 * and control), gated on the kill-switch only. When false the read is skipped. This is intentionally
	 * NOT the treatment-only flag: the read + exposure must run for control too so the experiment stays
	 * balanced.
	 */
	enabled: boolean;
};

export type UseLegacyInvitedAppShortcutsRecommendationsResult = {
	recommendedApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	resolverResult: UseInvitedAppShortcutsResult;
};

export function useLegacyInvitedAppShortcutsRecommendations({
	cloudId,
	userId,
	enabled,
}: UseLegacyInvitedAppShortcutsRecommendationsArgs): UseLegacyInvitedAppShortcutsRecommendationsResult {
	// Built from the shared builder so the values stay byte-identical to the preloader's — a mismatch
	// would miss the SSR-seeded interop cache record and trigger a client refetch.
	const variables = useMemo(
		() => buildInvitedAppShortcutsRecommendationsVariables(cloudId),
		[cloudId],
	);

	// Read through interop against the Apollo AGG client — the same path the legacy nav uses for its
	// own GlobalNavigationComponentQuery. Skipped for non-cohort users. On error/loading `data` is
	// undefined, which maps to no recommended apps (fails open to the plain menu).
	const { data } = useQueryInterop<
		InvitedAppShortcutsRecommendationsQuery$data,
		InvitedAppShortcutsRecommendationsQuery$variables
	>(InvitedAppShortcutsRecommendationsQueryNode, {
		interopApolloAggClient: true,
		variables,
		skip: !enabled,
	});

	// Map the translated recommendations into the typed allowed-target list. `error` and `empty`
	// results (and a not-yet-loaded `data`) all fall through to `[]`.
	const recommendedApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey> = useMemo(() => {
		if (!data) {
			return [];
		}
		const result = buildInvitedAppShortcutsRecommendationsResult(data);
		if (result.kind !== 'recommendations') {
			return [];
		}
		return result.recommendations.reduce<InvitedAppShortcutAllowedTargetAppKey[]>((acc, rec) => {
			if (isAllowedTargetAppForInvitedAppShortcuts(rec.entityId)) {
				acc.push(rec.entityId);
			}
			return acc;
		}, []);
	}, [data]);

	// Reuse the SAME process-singleton cookie cache as the experiment branch (shared across the legacy
	// and GSN surfaces). Productionisation does not read the cache for eligibility (that comes from
	// `recommendedApps`). Inline input (matching the GSN renderer) so the discriminated union narrows.
	const resolverResult = useInvitedAppShortcuts(
		enabled
			? {
					enabled: true,
					mode: 'productionisation',
					sourceProduct: SOURCE_APP,
					targetProducts: recommendedApps,
					cloudId,
					userId,
					cache: getInvitedAppShortcutsCache(),
					recommendedApps,
				}
			: { enabled: false },
	);

	// Fire the exposure whenever there is an actual recommendation to show — for BOTH experiment arms
	// (`enabled` is the whole gated cohort, not treatment-only). This is the balanced divergence point:
	// every eligible user who would be shown a recommendation is exposed, so treatment and control are
	// comparable. `expVal` fires the Statsig exposure (tagged `isEnabled` true for treatment / false for
	// control) and `fireFeatureExposed` emits the product-analytics event. Routing/render read the same
	// experiment with `UNSAFE_expValEqualsNoExposure` so this is the single exposure per page.
	useEffect(() => {
		if (enabled && recommendedApps.length > 0) {
			resolverResult.fireFeatureExposed({
				isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
			});
		}
	}, [enabled, recommendedApps, resolverResult]);

	return { recommendedApps, resolverResult };
}
